import { CircularProgress } from '@material-ui/core';
import { Stack, TextField, Button, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ButtonStyle } from '../../../../assets/styles/custom.styles';
import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import getErrorMessage from '../../../../assets/utils/getErrorMessage';
import { isEmailValid } from '../../../../assets/utils/helpers';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import ErrorResponseObject from '../../../../interfaces/errorResponseObject';
import RequestStatusObject from '../../../../interfaces/RequestStatusObject';

interface UpdateEmailProps {
  updateEmail: (email: string) => void;
}

const indicatorText = {
  color: '#878787',
  fontStyle: 'normal',
  fontSize: '16px',
  fontWeight: 400, 
  lineHeight: 'normal',
  minWidth: '168px',
};

const bodyText = {
  color: '#181818',
  fontStyle: 'normal',
  fontSize: '16px',
  fontWeight: 400, 
  lineHeight: 'normal',
};

const UpdateEmail = ({ updateEmail }: UpdateEmailProps ) => {
  const { slug, userId } = useParams();
  const { createToastNotification } = useToastNotificationContext();
  const [email, setEmail] = useState<string>('');
  const [requestStatus, setRequestStatus] = useState<RequestStatusObject>({
    isLoading: false,
    success: false,
    error: false,
    responseText: '',
  });

  const updateEmailHandler = useCallback(async () => {
    // CHECK IF EMAIL IS VALID
    if (!isEmailValid(email)) {
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        responseText: 'Email is not valid',
      });
      createToastNotification('Email is not valid', 'error');
      return;
    }

    try {
      setRequestStatus({
        isLoading: true,
        success: false,
        error: false,
        responseText: '',
      });
      const response = await axiosInstance().patch(`/confide/tenants/${slug}/users/${userId}/update-email`, { email: email });
      const res = response.data;
      updateEmail(res.email);
      setRequestStatus({
        isLoading: false,
        success: true,
        error: false,
        responseText: `Email successfully updated to ${res.email}`,
      });
      createToastNotification(`Email successfully updated to ${res?.email}`, 'success');
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err as ErrorResponseObject);
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        responseText: errorMessage,
      });
      createToastNotification(errorMessage, 'error');
    }
  }, [slug, userId, email, updateEmail, createToastNotification]);

  return (
    <Stack spacing={1}>
      <Stack direction='row' justifyContent='space-between' maxWidth={515}>
        <Typography sx={{
          ...bodyText,
          fontSize: '14px',
          fontWeight: 500,
        }}>Update Email</Typography>
        <Typography sx={{
          ...indicatorText,
          fontSize: '14px',
          fontWeight: 500,
          textAlign:'end',
        }}
        >Action</Typography>
      </Stack>
      
      <Stack direction='row' spacing={2}>
        <TextField 
          variant='standard'
          fullWidth
          sx={{
            width: '407px',
            height: '46px',
            padding: '6px 20px',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            borderRadius: '6px',
          }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          InputProps={{ disableUnderline: true }}
          placeholder='Enter Email Address'/>
        <Button sx={{
          ...ButtonStyle.gradientBorder,
          padding: '14px 24px',
          width: '100px',
        }}
        onClick={updateEmailHandler}>
          {requestStatus.isLoading ? 
              <CircularProgress size={20}/>
            :
            'Update'
          }</Button>
      </Stack>
    </Stack>
  );
};

export default UpdateEmail;